import type { AxiosPromise } from "axios";

import Api from "api/Api";
import type {
  MODULE_ENTITY_TYPE,
  MODULE_TYPE,
  Module,
  ModuleMetadata,
} from "ee/constants/ModuleConstants";
import type { ApiResponse } from "api/ApiResponses";
import type { Action } from "entities/Action";
import type { CreateJSCollectionRequest } from "ee/api/JSActionAPI";
import type { JSCollection } from "entities/JSCollection";
import type { NestedDSL } from "@shared/dsl";

interface FetchModuleActionsPayload {
  moduleId: string;
}

interface FetchModuleEntitiesPayload {
  moduleId: string;
}

export interface CreateModulePayload<TLayout = unknown> {
  packageId: string;
  type: MODULE_TYPE;
  name?: string;
  inputsForm?: Module["inputsForm"];
  outputsForm?: Module["outputsForm"];
  entity?: (Partial<Action> | CreateJSCollectionRequest) & {
    type: MODULE_ENTITY_TYPE;
  };
  layouts?: TLayout[];
}

export interface CreateModuleResponse extends Module {
  metadata: ModuleMetadata;
}

export interface DeleteModulePayload {
  id: string;
}

export type UpdateModuleActionsResponse = Action;

export type UpdateModuleJSCollectionResponse = JSCollection;

export type FetchModuleActionsResponse = Action[];

export interface FetchModuleEntitiesResponse {
  actions: Action[];
  jsCollections: JSCollection[];
}

export interface RefactorModulePayload {
  moduleId: string;
  newName: string;
  oldName: string;
}

export interface RefactorModuleInputPayload {
  moduleId: string;
  newName: string;
  oldName: string;
  inputId: string;
  layoutId?: string;
}

export type RefactorModuleResponse = Module;

export interface UpdateModuleLayoutPayload {
  layoutId: string;
  branchedModuleId: string;
  dsl: NestedDSL<unknown>;
  packageId: string;
}

interface FetchModuleByIdPayload {
  moduleId: string;
}

type FetchModuleByIdResponse = Module;

const BASE_URL = "v1/modules";

class ModuleApi extends Api {
  static async fetchModuleById(
    payload: FetchModuleByIdPayload,
  ): Promise<AxiosPromise<ApiResponse<FetchModuleByIdResponse>>> {
    const { moduleId } = payload;
    const url = `${BASE_URL}/${moduleId}`;

    return Api.get(url);
  }

  static async fetchActions(
    payload: FetchModuleActionsPayload,
  ): Promise<AxiosPromise<ApiResponse<FetchModuleActionsResponse>>> {
    const { moduleId } = payload;
    const url = `${BASE_URL}/${moduleId}/actions`;

    return Api.get(url);
  }

  static async getModuleEntities(
    payload: FetchModuleEntitiesPayload,
  ): Promise<AxiosPromise<ApiResponse<FetchModuleEntitiesResponse>>> {
    const { moduleId } = payload;
    const url = `${BASE_URL}/${moduleId}/entities`;

    return Api.get(url);
  }

  static async deleteModule(
    payload: DeleteModulePayload,
  ): Promise<AxiosPromise<ApiResponse>> {
    const { id } = payload;
    const url = `${BASE_URL}/${id}`;

    return Api.delete(url);
  }

  static async updateAction(
    payload: Action,
  ): Promise<AxiosPromise<ApiResponse<UpdateModuleActionsResponse>>> {
    const { id, moduleId } = payload;
    const url = `${BASE_URL}/${moduleId}/actions/${id}`;

    return Api.put(url, payload);
  }

  static async updateJSCollection(
    payload: JSCollection,
  ): Promise<AxiosPromise<ApiResponse<UpdateModuleJSCollectionResponse>>> {
    const { id, moduleId } = payload;
    const url = `${BASE_URL}/${moduleId}/jscollections/${id}`;

    return Api.put(url, payload);
  }

  static async updateModule(
    payload: Module,
  ): Promise<AxiosPromise<ApiResponse>> {
    const { id } = payload;
    const url = `${BASE_URL}/${id}`;

    return Api.put(url, payload);
  }

  static async updateModuleLayout(
    payload: UpdateModuleLayoutPayload,
  ): Promise<AxiosPromise<ApiResponse>> {
    const { branchedModuleId, dsl, layoutId, packageId } = payload;
    const url = `v1/layouts/${layoutId}/modules/${branchedModuleId}?packageId=${packageId}`;

    const body = {
      dsl,
    };

    return Api.put(url, body);
  }

  static async refactorModuleInput(
    payload: RefactorModuleInputPayload,
  ): Promise<AxiosPromise<ApiResponse>> {
    const url = `${BASE_URL}/refactor/input`;

    return Api.put(url, payload);
  }

  static async createModule(
    payload: CreateModulePayload,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.post(BASE_URL, payload);
  }

  static async refactorModule(
    payload: RefactorModulePayload,
  ): Promise<AxiosPromise<ApiResponse<RefactorModuleResponse>>> {
    const url = `${BASE_URL}/refactor`;

    return Api.put(url, payload);
  }
}

export default ModuleApi;
